import React from 'react';
import { Box, Container, Typography, Grid, Stack } from '@mui/material';
import { styled, keyframes } from '@mui/system';

// Definicja animacji
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Dane kroków
const steps = [
  {
    text: '1. Wgraj rzuty budynku',
    icon: '/img/istep1.svg',
    boxColor: '#1C353A',
    iconColor: '#E84055',
    startCol: 4,
  },
  {
    text: '2. Ustaw niezbędne parametry',
    icon: '/img/istep2.svg',
    boxColor: '#E84055',
    iconColor: '#1C353A',
    startCol: 5,
  },
  {
    text: '3. Uruchom proces rozpoznawania elementów',
    icon: '/img/istep3.svg',
    boxColor: '#1C353A',
    iconColor: '#E84055',
    startCol: 3,
  },
  {
    text: '4. Zweryfikuj wyniki',
    icon: '/img/istep4.svg',
    boxColor: '#E84055',
    iconColor: '#1C353A',
    startCol: 5,
  },
  {
    text: (
      <>
        5. Pobierz zestawienia i model IFC 
        wraz z przygotowanym raportem
      </>
    ),
    icon: '/img/istep5.svg',
    boxColor: '#1C353A',
    iconColor: '#E84055',
    startCol: 4.5,
  },
];

// Wrapper sekcji
const SectionWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  backgroundColor: theme.palette.background.default,
  paddingTop: '120px',
  paddingBottom: '120px',
}));

// Nagłówek z podkreśleniem
const UnderlinedHeading = styled('span')({
  position: 'relative',
  zIndex: 1,
  display: 'inline-block',
  '&::after': {
    content: '""',
    position: 'absolute',
    zIndex: -1,
    right: 0,
    bottom: 0,
    width: '30%',
    height: '30px',
    backgroundImage: 'url(/img/redunderline.svg)',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'bottom center',
  },
});

// Box kroków jako równoległobok
const TextBox = styled(Box)(({ color }) => ({
  backgroundColor: color,
  clipPath: 'polygon(20px 0%, 100% 0%, calc(100% - 20px) 100%, 0% 100%)',
  height: '90px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 60px',
  color: '#fff',
  fontSize: '26px',
  fontWeight: 'bold',
  '@media (max-width: 600px)': {
    fontSize: '14px',
    width: '60vw',
    padding: '0 30px',
      height: '60px',
  },
}));

// Box ikon również jako równoległobok
const IconBox = styled(Box)(({ color }) => ({
  width: '120px',
  height: '90px',
  backgroundColor: color,
  clipPath: 'polygon(20px 0%, 100% 0%, calc(100% - 20px) 100%, 0% 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  right: '18px',
    '@media (max-width: 600px)': {
   
      height: '60px',
  },
}));

// Komponent Section2 bez użycia React.FC
const Section2 = () => (
  <SectionWrapper component="section" id="about" sx={{ scrollMarginTop: '100px' }}>
    {/* Znak wodny z lewej strony */}
    <Box
      component="img"
      src="/img/bgpicture.png"
      alt="bg watermark"
      sx={{
        position: 'absolute',
        right: 0,
        top: '10%',
        width: '48%',
        zIndex: 0,
      }}
    />

    <Container maxWidth="xl">
      <Typography
        variant="h2"
        align="center"
        sx={{
          fontSize: { xs: '28px', md: '38px' },
          fontWeight: 'bold',
          color: 'primary.main',
          mb: 4,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <UnderlinedHeading>Bimetria – jak to działa?</UnderlinedHeading>
      </Typography>
<Typography
  variant="body1"
  align="center"
  sx={{
    fontSize: { xs: '18px', md: '29px' },
    color: 'text.primary',
    // domyślnie ograniczamy szerokość do 900px
    maxWidth: { xs: '900px', lg: 'none' },
    // szerokość: 100% na mobile/tablet, 60% na lg+
    width: { xs: '100%', lg: '40%' },
    mx: 'auto',   // wyśrodkowanie
    mb: 10,
    position: 'relative',
    zIndex: 1,
  }}
>
  Bimetria to aplikacja tworząca modele 3D budynków na podstawie rzutów 2D. <br />
  Dzięki niej możesz stworzyć model BIM<br /> i wygenerować zestawienia materiałów
  budowlanych w kilku prostych krokach.
</Typography>

      {steps.map((step, index) => {
        const isThird = index === 2;
        const isLast = index === steps.length - 1;
        const side = index % 2 === 0 ? 'left' : 'right';
        const offset = isThird ? '80%' : '90%';
        const pathStyle =
          index < 4
            ? {
                zIndex: 1,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: isThird ? '75%' : '50%',
                  height: '100px',
                  width: '100px',
                  backgroundImage: `url(/img/path${index + 1}.svg)`,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  zIndex: -1,
                  [side]: offset,
                  '@media (max-width: 600px)': {
                    display: 'none',
                  },
                },
              }
            : {};

        return (
          <Grid
            container
            key={index}
            sx={{
              mb: isLast ? 0 : 6,
              pl: { xs: '5%', md: `${(step.startCol / 12) * 100}%` },
              alignItems: 'center',
              opacity: 0,
              animation: `${fadeInUp} 0.6s ease-out forwards`,
              animationDelay: `${index * 0.3}s`,
            }}
          >
            <Grid item>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.25}
                sx={{ position: 'relative', ...pathStyle }}
              >
                <TextBox
                  color={step.boxColor}
                  sx={
                    isLast
                      ? { fontSize: { xs: '12px', md: '26px' } }
                      : {}
                  }
                >
                  {step.text}
                </TextBox>
               <IconBox
    color={step.iconColor}
    sx={
      isLast
        ? {
            clipPath:
              'polygon(20px 0%, 100% 0%, calc(100% - 20px) 100%, 0% 100%)',
          }
        : {}
    }
  >
    <Box
      component="img"
      src={step.icon}
      alt={`Krok ${index + 1}`}
      sx={(theme) => ({
        // podstawowe wymiary
        width: isLast ? '90px' : '75px',
        height: isLast ? '66px' : '55px',
        // na mobile (<=600px) pomniejszamy o 30%
        [theme.breakpoints.down('sm')]: {
          width: isLast ? `${90 * 0.7}px` : `${75 * 0.7}px`,
          height: isLast ? `${66 * 0.7}px` : `${55 * 0.7}px`,
        },
      })}
    />
  </IconBox>
              </Stack>
            </Grid>
          </Grid>
        );
      })}
    </Container>
  </SectionWrapper>
);

export default Section2;
